import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { defaultListingStorageForModel, parseStorageLabelToGb } from '@redevicer/shared'
import { listingsAPI, pricingAPI } from '../api/client'
import ListingModelPicker from './ListingModelPicker'

type PhoneModel = { id: number; manufacturer: string; model_name: string }

const CONDITIONS = [
  { value: 'excellent', labelKey: 'trust.valuation.condition.excellent' },
  { value: 'good', labelKey: 'trust.valuation.condition.good' },
  { value: 'fair', labelKey: 'trust.valuation.condition.fair' },
] as const

interface Props {
  onSell: () => void
}

export default function InstantValuation({ onSell }: Props) {
  const { t } = useTranslation()
  const [models, setModels] = useState<PhoneModel[]>([])
  const [modelId, setModelId] = useState('')
  const [modelsLoading, setModelsLoading] = useState(true)
  const [condition, setCondition] = useState('good')
  const [estimate, setEstimate] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setModelsLoading(true)
    listingsAPI
      .getPhoneModels()
      .then((res) => {
        const list = (res.data as PhoneModel[]) ?? []
        setModels(list)
      })
      .finally(() => setModelsLoading(false))
  }, [])

  async function runEstimate() {
    const id = Number(modelId)
    if (!id) return
    setBusy(true)
    setError('')
    try {
      const selected = models.find((m) => m.id === id)
      const storageLabel = selected
        ? defaultListingStorageForModel(selected.manufacturer, selected.model_name)
        : null
      const storageGb = storageLabel ? parseStorageLabelToGb(storageLabel) : undefined
      const res = await pricingAPI.suggest({
        phone_model_id: id,
        condition,
        battery_health: 85,
        storage_gb: storageGb,
      })
      const val = Number(res.data?.recommended_eur ?? res.data?.balanced_eur)
      if (Number.isFinite(val) && val > 0) setEstimate(Math.round(val))
      else setError(t('trust.valuation.error'))
    } catch {
      setError(t('trust.valuation.error'))
    } finally {
      setBusy(false)
    }
  }

  const showResultSlot = busy || estimate != null

  return (
    <section
      id="vc-instant-valuation"
      className="vc-valuation vc-section vc-section--centered vc-section--valuation"
      aria-labelledby="vc-valuation-heading"
    >
      <h3 id="vc-valuation-heading" className="vc-section-title">
        {t('trust.valuation.title')}
      </h3>
      <p className="vc-section-lead">{t('trust.valuation.lead')}</p>
      <div className="vc-valuation-card">
        <div className="vc-valuation-field">
          <span id="vc-valuation-model-label" className="vc-valuation-label">
            {t('trust.valuation.model')}
          </span>
          <ListingModelPicker
            models={models}
            value={modelId}
            onChange={setModelId}
            loading={modelsLoading}
            collapsible
            placeholder={t('trust.valuation.modelPick', 'Pasirink modelį')}
          />
        </div>
        <div className="vc-valuation-field">
          <span id="vc-valuation-condition-label" className="vc-valuation-label">
            {t('trust.valuation.conditionLabel')}
          </span>
          <div
            className="vc-valuation-condition"
            role="radiogroup"
            aria-labelledby="vc-valuation-condition-label"
          >
            {CONDITIONS.map((c) => {
              const on = condition === c.value
              return (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={`vc-valuation-condition__opt${on ? ' vc-valuation-condition__opt--on' : ''}`}
                  onClick={() => setCondition(c.value)}
                >
                  {t(c.labelKey)}
                </button>
              )
            })}
          </div>
        </div>
        <p className="vc-valuation-criteria">{t('trust.valuation.criteriaHint')}</p>
        <button
          type="button"
          className="vc-btn vc-btn-outline vc-valuation-calc"
          disabled={busy || !modelId}
          onClick={runEstimate}
        >
          {busy ? '…' : t('trust.valuation.cta')}
        </button>
        <div
          className={`vc-valuation-result-panel${showResultSlot ? ' vc-valuation-result-panel--active' : ''}`}
          aria-live="polite"
        >
          {showResultSlot ? (
            <div className="vc-valuation-result-box">
              {busy ? (
                <p className="vc-valuation-result vc-valuation-result--pending">
                  {estimate != null ? (
                    <>
                      ~€{estimate}
                      <span className="vc-valuation-result-sub">{t('trust.valuation.resultHint')}</span>
                    </>
                  ) : (
                    <span className="vc-valuation-result-pending">{t('trust.valuation.cta')}…</span>
                  )}
                </p>
              ) : estimate != null ? (
                <p className="vc-valuation-result">
                  ~€{estimate}
                  <span className="vc-valuation-result-sub">{t('trust.valuation.resultHint')}</span>
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        {error ? <p className="vc-valuation-error">{error}</p> : null}
      </div>
      <button type="button" className="vc-btn vc-btn-primary vc-valuation-sell" onClick={onSell}>
        {t('trust.valuation.sellCta')}
      </button>
    </section>
  )
}
